/**
 * Audit Trail Storage Abstraction
 *
 * Provides storage layer for audit logs with cloud sync support.
 * Routes through storageRouter (IndexedDB in prod, localStorage fallback) —
 * the SAME persistence layer every other module uses (see useStorageState.js)
 * — instead of talking to raw localStorage directly.
 *
 * FT-515 fix (2026-09-29): this module previously read/wrote raw localStorage
 * under a PER-MODULE key (`audit_log_{module}_{orgId}_{vesselId}`) that nothing
 * else in the app ever wrote to, so reads always came back empty even though
 * writes appeared to succeed. The real synced audit data (confirmed live via
 * server + IndexedDB) lives under ONE COMBINED per-vessel key
 * (`audit_log_{orgId}_{vesselId}` — no module segment) holding entries from
 * ALL modules mixed together, each tagged with its own `.module` field. That
 * key shape is produced by sync/UniversalSyncBridge.js's ARRAY_COLLECTIONS
 * branch (`toOrgScopedKey('audit_log_' + vesselId)`) via the
 * `audit_log: 'sr.audit_log.v1'` mapping in sync/moduleMap.js. This module now
 * reads/writes that SAME combined key and filters by `entry.module`
 * client-side to get per-module results.
 *
 * NOTE: loadAuditLog / saveAuditLog / appendAuditEntry / getAllAuditLogs /
 * clearAuditLog are now ASYNC (storageRouter.readJSON/writeJSON are Promises).
 *
 * @module utils/auditStorage
 */

import { toOrgScopedKey } from '../lib/storage';
import { readJSON, writeJSON } from '../lib/storageRouter';

/**
 * Build the combined per-vessel storage key that UniversalSyncBridge writes
 * to for the audit_log collection. No module segment — every module's audit
 * entries share this one key, disambiguated by each entry's own `.module`.
 *
 * FT-515 round 3: exported so useAuditTrail.js can match `sr:storage:set`
 * event.detail.key against the SAME key this module reads/writes, instead of
 * re-deriving the key construction in a second place and risking drift.
 * @param {string} vesselId
 * @returns {string}
 */
export const combinedAuditKey = (vesselId) => toOrgScopedKey(`audit_log_${vesselId}`);

/**
 * FT-515 round 2: per-key promise chain so concurrent read-merge-write cycles
 * against the SAME combined audit key never interleave.
 *
 * Both saveAuditLog and appendAuditEntry do read-combined -> merge -> write
 * against this one shared key. Without serialising them, two concurrent calls
 * (e.g. an equipment append and a maintenance append firing together, or two
 * same-module appends) can both read the same "before" snapshot; whichever
 * write lands last silently overwrites the other's entry — an entry vanishes
 * from storage and the next entry's `previousHash` chain-link no longer
 * points at a real prior entry, breaking the tamper-evident hash chain.
 *
 * Each queued task waits for the previous task for that key to fully finish
 * (read + merge + write) before it starts its own read. Same-tab/same-process
 * only — cross-tab races are explicitly out of scope here.
 *
 * FT-515 round 5: exported so sync/UniversalSyncBridge.js's
 * mergeAndPersistAuditLogPull can serialise a cloud PULL-merge against this
 * SAME per-key queue, not just same-tab SAVEs. A pull-merge does the identical
 * read-combined -> merge -> write shape against the identical combined key, so
 * without sharing this lock a pull landing concurrently with a same-tab
 * appendAuditEntry/saveAuditLog can interleave with it exactly like two saves
 * racing each other — one write clobbers the other's "before" snapshot.
 *
 * @param {string} key - resolved combined storage key
 * @param {() => Promise<any>} task
 * @returns {Promise<any>} the task's own result/rejection
 */
const auditKeyQueues = new Map();
export function withAuditKeyLock(key, task) {
  const previous = auditKeyQueues.get(key) || Promise.resolve();
  const result = previous.then(task, task);
  // The queue itself must never wedge on a rejection — only the caller's
  // returned `result` promise carries the real success/failure.
  auditKeyQueues.set(key, result.then(() => undefined, () => undefined));
  return result;
}

/**
 * Save audit log entries for a module.
 *
 * Reads the existing combined (all-modules) array first and replaces only
 * this module's slice with `entries`, so other modules' entries already
 * cached in the same key are preserved rather than clobbered. Runs under the
 * per-key lock so a concurrent save/append for the same vessel can't race it.
 *
 * @param {string} module - Module name (e.g., 'equipment', 'crew', 'defects')
 * @param {string} vesselId - Vessel ID for scoping
 * @param {Array} entries - This module's full array of audit log entries
 * @returns {Promise<boolean>} Success status
 */
export const saveAuditLog = async (module, vesselId, entries) => {
  const key = combinedAuditKey(vesselId);
  try {
    return await withAuditKeyLock(key, async () => {
      const existingCombined = (await readJSON(key, [])) || [];
      const otherModulesEntries = existingCombined.filter(e => e?.module !== module);
      const merged = [...otherModulesEntries, ...entries];

      // v4.2 Days 16-17: writeJSON (via storageRouter) triggers cloud sync —
      // same event-dispatch path as before, just through the real persistence
      // layer instead of raw localStorage.
      return await writeJSON(key, merged);
    });
  } catch (error) {
    console.error('Failed to save audit log:', error);
    return false;
  }
};

/**
 * Load audit log entries for a module
 * @param {string} module - Module name
 * @param {string} vesselId - Vessel ID for scoping
 * @returns {Promise<Array>} Array of audit log entries for this module
 */
export const loadAuditLog = async (module, vesselId) => {
  try {
    const key = combinedAuditKey(vesselId);
    const combined = (await readJSON(key, [])) || [];
    return combined.filter(e => e?.module === module);
  } catch (error) {

    return [];
  }
};

/**
 * Append a single entry to the audit log.
 *
 * Does its own read-merge-write under the per-key lock (rather than calling
 * loadAuditLog + saveAuditLog as two separate locked operations) so the whole
 * "read current entries, add this one, write back" cycle is a single atomic
 * step — that's what makes two concurrent appendAuditEntry calls for the same
 * vessel (same module or different modules) both survive instead of one
 * clobbering the other.
 *
 * @param {string} module - Module name
 * @param {string} vesselId - Vessel ID for scoping
 * @param {Object} entry - Audit log entry to append
 * @returns {Promise<boolean>} Success status
 */
export const appendAuditEntry = async (module, vesselId, entry) => {
  const key = combinedAuditKey(vesselId);
  try {
    return await withAuditKeyLock(key, async () => {
      const existingCombined = (await readJSON(key, [])) || [];
      const otherModulesEntries = existingCombined.filter(e => e?.module !== module);
      const thisModuleEntries = existingCombined.filter(e => e?.module === module);
      const merged = [...otherModulesEntries, ...thisModuleEntries, entry];
      return await writeJSON(key, merged);
    });
  } catch (error) {

    return false;
  }
};

/**
 * Get all audit logs across all modules for a vessel
 * @param {string} vesselId - Vessel ID
 * @param {Array<string>} modules - Optional array of module names to include
 * @returns {Promise<Array>} Combined audit log entries from all modules
 */
export const getAllAuditLogs = async (vesselId, modules = []) => {
  try {
    const key = combinedAuditKey(vesselId);
    let allEntries = (await readJSON(key, [])) || [];

    if (modules.length > 0) {
      const moduleSet = new Set(modules);
      allEntries = allEntries.filter(e => moduleSet.has(e?.module));
    }

    // Sort by timestamp descending (newest first)
    return allEntries.sort((a, b) =>
      new Date(b.timestamp) - new Date(a.timestamp)
    );
  } catch (error) {

    return [];
  }
};

/**
 * Clear audit log for a module (use with caution!)
 * @param {string} module - Module name
 * @param {string} vesselId - Vessel ID
 * @returns {Promise<boolean>} Success status
 */
export const clearAuditLog = async (module, vesselId) => {
  try {
    const key = combinedAuditKey(vesselId);
    const existingCombined = (await readJSON(key, [])) || [];
    const remaining = existingCombined.filter(e => e?.module !== module);
    return await writeJSON(key, remaining);
  } catch (error) {

    return false;
  }
};
